/**
 * EU-408 — builders for the candidate-app URLs a consultor shares as the
 * "private link" of a program / job.
 *
 * The candidate SPA routes are PLURAL — `/programas/:id` and `/vagas/:id`
 * (see apps/candidate/frontend/src/routes). A consultor publishes a program
 * (and its jobs) as private, copies the link here, and sends it to the
 * contracting company to preview before the public launch. Centralised so the
 * route shape lives in a single, testable place.
 *
 * `baseUrl` is the candidate origin resolved per serving domain in
 * `config/env.ts` (`recruiterEnv.candidateAppUrl`).
 */
import { toast } from '@eureca/ui';

function joinBase(baseUrl: string): string {
  return (baseUrl ?? '').replace(/\/+$/, '');
}

/** Private link to a program's candidate page: `${base}/programas/:id`. */
export function buildCandidateProgramUrl(baseUrl: string, programId: string): string {
  return `${joinBase(baseUrl)}/programas/${programId}`;
}

/** Private link to a job's candidate page: `${base}/vagas/:id`. */
export function buildCandidateJobUrl(baseUrl: string, jobId: string): string {
  return `${joinBase(baseUrl)}/vagas/${jobId}`;
}

/**
 * EU-408 — copy a private candidate link to the clipboard and toast. Shared by
 * the program list, program edit shell, and job list actions; mirrors the
 * ConsultantCopyableId clipboard UX (`<label> copiado` + close action).
 */
export async function copyPrivateLink(url: string, label: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(url);
    toast(`${label} copiado`, { action: { label: 'Fechar', onClick: () => undefined } });
  } catch {
    toast.error('Não foi possível copiar o link. Tente novamente.');
  }
}

/**
 * EU-408 — open the private candidate link in a new tab so the consultor can
 * preview the program / job exactly as the contracting company will see it.
 * `noopener,noreferrer` keeps the opened page from reaching back via
 * `window.opener`.
 */
export function openPrivateLink(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}
